import React, { Dispatch, Fragment, SetStateAction, useState } from 'react';
import Drawer from '../../../common/components/Drawer/Drawer';
import { availableCarriers, calculateMovesPercentage, getCarrierById } from '../../../common/helpers/carriers';
import { formattedNumberToInt } from '../../../common/helpers/utils';
import { AdjustableComponent, useClasses } from '../../../common/hooks/useClasses';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import FormikInputField from '../../../common/components/FormikInputField/FormikInputField';
import {
  McButton,
  McDateRange,
  McInputDate,
  McNotification,
  McOption,
  McPopover,
  McSelect,
  McTab,
  McTabBar,
  McTable,
} from '@maersk-global/mds-react-wrapper';
import z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import FormattedMessage from '../../../common/FormattedMessage';
import {
  addNewCarrier,
  editCarriersMapper,
  fillDefaultCarriers,
  recalculateEntries,
  removeCarrier,
  updateEntries,
  validateEntries,
} from '../../config/mappers';
import useCapacityData from '../../hooks/useCapacityData';
import useCarrierColumnsHeader from '../../hooks/useCarrierColumnsHeader';
import { createDefaultValueDto } from '../dto/DefaultValueDto';
import { modifyDate } from '../../../common/helpers/modify-date';
import IMOInputField from '../IMODescription/IMOInputField';
import { editCarriersTableColumns } from '../soc/const';

export type AddDefaultValuesDrawerStyles = {
  field: string;
  buttons: string;
  form: string;
};

export type AddDefaultValuesDrawerProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSuccess?: Function;
  selectedTerminal: string;
  id: string;
  defaultValues: Map<string, number>;
  type: 'soc' | 'yard';
};

const FormValidationSchemaSOC = z
  .object({
    terminalCode: z.string(),
    moves: z.number({ message: 'Value should be a number' }).positive(),
    moveCountStretchPercentage: z.number({ message: 'Value should be a number (0 - 100)' }).min(0).max(100),
    moveCountCriticalPercentage: z.number({ message: 'Value should be a number (0 - 100)' }).min(0).max(100),
    entries: z.array(
      z.object({
        moves: z.number().gte(0, 'Movecount must be greater than or equal 0'),
        capacityDataType: z.string(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    // @ts-ignore
    if (!validateEntries(data.entries, data.moves)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Residual value can not be less than 0',
        path: ['entries'],
      });
    }
  });

const FormValidationSchemaYard = z.object({
  terminalCode: z.string(),
  oogUnits: z.number({ message: 'Value should be a number' }).positive(),
  imoUnits: z.number({ message: 'Value should be a number' }).positive(),
  yardCapacity: z.number({ message: 'Value should be a number' }).positive(),
  reeferPlugs: z.number({ message: 'Value should be a number' }).positive(),
});

const AddDefaultValuesDrawer: AdjustableComponent<AddDefaultValuesDrawerProps, AddDefaultValuesDrawerStyles> = ({
  classes,
  open,
  setOpen,
  onSuccess,
  selectedTerminal,
  id,
  defaultValues,
  type,
}) => {
  const styles = useClasses(
    {
      field: 'AddDefaultValuesDrawer__field',
      buttons: 'AddDefaultValuesDrawer__buttons',
      form: 'AddDefaultValuesDrawer__form-wrapper',
    },
    classes,
  );
  const [msgShown, setMsgShown] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [alertShown, setAlertShown] = useState(false);
  const { createTemplate } = useCapacityData();
  const { getCustomHeaders } = useCarrierColumnsHeader();

  const onClose = () => {
    setOpen(false);
  };

  const getFormInitialValues = () => {
    const baseValues = {
      id: id,
      terminalCode: selectedTerminal,
      startDate: defaultValues['startDate'],
      previousStartDate: defaultValues['previousStartDate'],
      nextStartDate: defaultValues['nextStartDate'],
      capacityDataType: 'TOTAL',
    };
    const socValues = {
      moves: defaultValues['moves'],
      moveCountStretchPercentage: defaultValues['moveCountStretchPercentage'],
      moveCountCriticalPercentage: defaultValues['moveCountCriticalPercentage'],
      entries:
        defaultValues['entries'].length > 1
          ? defaultValues['entries']
          : fillDefaultCarriers(
              defaultValues['moveCountStretchPercentage'],
              defaultValues['moveCountCriticalPercentage'],
            ),
    };
    const yardValues = {
      yardCapacity: defaultValues['yardCapacity'],
      reeferPlugs: defaultValues['reeferPlugs'],
      imoUnits: defaultValues['imoUnits'],
      imoUnitsDescription: defaultValues['imoUnitsDescription'],
      oogUnits: defaultValues['oogUnits'],
      ladenCapacity: defaultValues['ladenCapacity'],
      emptyCapacity: defaultValues['emptyCapacity'],
      maxCapacity: defaultValues['maxCapacity'],
      entries: defaultValues['entries'],
    };
    return { ...baseValues, ...(type === 'soc' ? socValues : yardValues) };
  };

  const onSubmit = async (values, { setSubmitting }) => {
    if (activeTab === 0) {
      setAlertShown(true);
      setActiveTab(1);
      return;
    }
    setSubmitting(true);
    const dto = createDefaultValueDto(values, type);
    try {
      await createTemplate(dto);
      onSuccess?.();
      onClose();
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer title={'Add default values'} open={open} setOpen={setOpen} onRequestClose={onClose} noFooter wider>
      <Formik
        initialValues={getFormInitialValues()}
        enableReinitialize
        // @ts-ignore
        validationSchema={toFormikValidationSchema(type === 'soc' ? FormValidationSchemaSOC : FormValidationSchemaYard)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => {
          const carrierData =
            type === 'soc'
              ? editCarriersMapper(
                  type,
                  // @ts-ignore
                  values.entries,
                  {
                    // @ts-ignore
                    totalMoveCount: values.moves,
                    // @ts-ignore
                    stretch: values.moveCountStretchPercentage,
                    // @ts-ignore
                    critical: values.moveCountCriticalPercentage,
                  },
                )
              : [];
          return (
            <Form className="EditDefaultValuesDrawer__form">
              <div className="EditDefaultValuesDrawer__form__wrapper">
                <McTabBar tabchange={(e) => setActiveTab(e.detail)} currentindex={activeTab}>
                  <McTab slot="tab" label="Default values" />
                  <div slot="panel" style={{ paddingBottom: 0, marginBottom: -16 }}>
                    <div>
                      {type === 'soc' ? (
                        <div style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', borderRadius: '0.25rem' }}>
                          <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
                            {/* Move Count */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <label>Move count</label>
                              <Field
                                label={''}
                                type="number"
                                name="moves"
                                component={FormikInputField}
                                input={(e) => {
                                  setFieldValue('moves', parseInt(e.target.value));
                                  setFieldValue(
                                    'entries',
                                    recalculateEntries(
                                      // @ts-ignore
                                      values.entries,
                                      // @ts-ignore
                                      getFormInitialValues().entries,
                                      // @ts-ignore
                                      getFormInitialValues().moves,
                                      e.target.value,
                                    ),
                                  );
                                }}
                              />
                              <ErrorMessage name="moves" component="div" />
                            </div>

                            {/* Threshold #1 with Icon and Total */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ backgroundColor: '#FFD029', width: '1rem', height: '1rem' }}></span>
                                <label>Threshold L2</label>
                              </div>
                              <Field
                                label={''}
                                type="number"
                                name="moveCountStretchPercentage"
                                suffix="%"
                                component={FormikInputField}
                              />
                              <ErrorMessage name="moveCountStretchPercentage" component="div" />
                              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#8A8A90' }}>
                                {/*//@ts-ignore*/}
                                Total: {printTotal(values.moves, values.moveCountStretchPercentage)}
                              </div>
                            </div>

                            {/* Threshold #2 with Icon and Total */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ backgroundColor: '#FF6000', width: '1rem', height: '1rem' }}></span>
                                <label>Threshold L3</label>
                              </div>
                              <Field
                                label={''}
                                type="number"
                                name="moveCountCriticalPercentage"
                                suffix="%"
                                component={FormikInputField}
                              />
                              <ErrorMessage name="moveCountCriticalPercentage" component="div" />
                              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#8A8A90' }}>
                                {/*// @ts-ignore*/}
                                Total: {printTotal(values.moves, values.moveCountCriticalPercentage)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <Field
                              label={'Yard TEU capacity'}
                              type="number"
                              name="yardCapacity"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="yardCapacity" component="div" />
                            <Field
                              label={'Reefer plugs'}
                              type="number"
                              name="reeferPlugs"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="reeferPlugs" component="div" />
                          </div>

                          <br />
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <IMOInputField isDrawerOpen={open} />
                            <Field label={'OOG units'} type="number" name="oogUnits" component={FormikInputField} />
                            <ErrorMessage name="oogUnits" component="div" />
                          </div>

                          <br />
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <Field label="Laden TEUs" type="number" name="ladenCapacity" component={FormikInputField} />
                            <ErrorMessage name="ladenCapacity" component="div" />
                            <Field
                              label={'Empty TEUs'}
                              type="number"
                              name="emptyCapacity"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="emptyCapacity" component="div" />
                            <Field
                              label={'Max TEU Capacity'}
                              type="number"
                              name="maxCapacity"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="maxCapacity" component="div" />
                          </div>
                        </>
                      )}
                      <br />
                      <div>
                        <h4>Validity period</h4>
                        {values.previousStartDate && (
                          <>
                            <McNotification
                              appearance="info"
                              body={
                                'Previous period: From ' +
                                values.previousStartDate +
                                ' to ' +
                                modifyDate(values.startDate?.split('T')[0], 'remove')
                              }
                              icon="info-circle"
                              verticalalign="middle"
                              actionsposition="right"
                            />
                            <br />
                          </>
                        )}
                        {/* Validity start date */}
                        <McDateRange>
                          yearcap={20}
                          {/*set 'value'*/}
                          <McInputDate
                            slot={'to'}
                            label={'To'}
                            disabled
                            value={values.nextStartDate || '2100-01-01'}
                          ></McInputDate>
                          {/*TODO: min/max to set the range*/}
                          <McInputDate
                            slot={'from'}
                            label={'From'}
                            showweeknumbers={true}
                            onMouseEnter={() => setMsgShown(true)}
                            onMouseLeave={() => setMsgShown(false)}
                            customize={[
                              {
                                date: (date) => date.getDay() === 0 || (date.getDay() <= 6 && date.getDay() >= 2),
                                disabled: true,
                              },
                            ]}
                            min={values.previousStartDate && modifyDate(values.previousStartDate, 'add', 4)}
                            disabled={values.previousStartDate === undefined}
                            value={values.startDate?.split('T')[0]}
                            input={(e) => {
                              // @ts-ignore
                              setFieldValue('startDate', e.target.value + 'T00:00:00Z');
                            }}
                          ></McInputDate>
                        </McDateRange>
                        <div style={{ position: 'relative', height: 50, marginTop: 10 }}>
                          {msgShown && (
                            <div style={{ position: 'absolute', minWidth: '100%' }}>
                              <McNotification
                                appearance="warning"
                                body="For periods shorter than 5 weeks, please adjust the individual weeks"
                                icon="exclamation-circle"
                                verticalalign="middle"
                                actionsposition="right"
                                fit="small"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {type === 'soc' && (
                    <>
                      <McTab slot="tab" label="Carriers" />
                      <div slot="panel">
                        <div className="EditDefaultValuesDrawer__add-carrier">
                          <McPopover
                            trigger="click"
                            width="auto"
                            modalmode="none"
                            position="bottom-right"
                            opendelay={50}
                          >
                            <McButton
                              slot="trigger"
                              label="Add new carrier"
                              icon="plus"
                              variant="outlined"
                              fit="small"
                            />
                            <div className="EditDefaultValuesDrawer__add-carrier-popoup">
                              <McSelect
                                label="Carrier"
                                placeholder="Select carrier"
                                name="carrier"
                                optionselected={(e) =>
                                  // @ts-ignore
                                  setFieldValue('entries', addNewCarrier(values.entries, e.target.value))
                                }
                              >
                                {availableCarriers
                                  .filter((c) => !c.required && !c.deprecated)
                                  .map((c) => (
                                    <McOption
                                      key={c.id}
                                      value={c.id}
                                      // @ts-ignore
                                      disabled={values.entries.some((x) => x.capacityDataType === c.id)}
                                    >
                                      {c.name}
                                    </McOption>
                                  ))}
                              </McSelect>
                            </div>
                          </McPopover>
                        </div>
                        {alertShown && (
                          <div className="EditDefaultValuesDrawer__carrier-warning">
                            <McNotification
                              appearance="warning"
                              body="Please confirm carriers' move count before submitting"
                              icon="exclamation-triangle"
                              verticalalign="middle"
                              actionsposition="right"
                              fit="small"
                            />
                          </div>
                        )}
                        <McTable columns={editCarriersTableColumns} data={carrierData}>
                          {getCustomHeaders().map((header) => (
                            <div key={header.id} slot={`${header.id}_header`}>
                              {header.render()}
                            </div>
                          ))}
                          {carrierData.map((row, index) => {
                            const carrier = getCarrierById(row.carrier);
                            return (
                              <Fragment key={`${row.id}_actions`}>
                                <div slot={`${row.id}_carrier`}>
                                  <div className="TerminalCarriersRenderer__logo">
                                    {carrier.image && <carrier.image />}
                                    <span className="TerminalCarriersRenderer__logo__name">{carrier.name}</span>
                                  </div>
                                </div>
                                <div slot={`${row.id}_moveCount`}>
                                  {carrier.order > -1 ? (
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <Field
                                        hiddenlabel
                                        name="moveCount"
                                        type="number"
                                        fit="small"
                                        component={FormikInputField}
                                        value={row.moveCount}
                                        input={(e: InputEvent) =>
                                          setFieldValue(
                                            'entries',
                                            // @ts-ignore
                                            updateEntries(values.entries, carrier.id, 'moves', e.target.value),
                                          )
                                        }
                                        min={0}
                                        width={100}
                                      />
                                      <Field
                                        hiddenlabel
                                        type="number"
                                        name="moveCountPercentage"
                                        suffix="%"
                                        component={FormikInputField}
                                        min={0}
                                        fit="small"
                                        width={80}
                                        value={calculateMovesPercentage(
                                          formattedNumberToInt(`${carrierData[0].moveCount}`),
                                          row.moveCount,
                                        )}
                                        input={(e: InputEvent) =>
                                          setFieldValue(
                                            'entries',
                                            updateEntries(
                                              // @ts-ignore
                                              values.entries,
                                              carrier.id,
                                              'percentage',
                                              // @ts-ignore
                                              e.target.value,
                                              formattedNumberToInt(`${carrierData[0].moveCount}`),
                                            ),
                                          )
                                        }
                                      />
                                    </div>
                                  ) : (
                                    row.moveCount
                                  )}
                                </div>
                                <div slot={`${row.id}_actions`}>
                                  {!carrier.required && carrier.order > -1 && (
                                    <McButton
                                      label="Delete"
                                      icon="trash"
                                      fit="small"
                                      variant="plain"
                                      appearance="primary"
                                      // @ts-ignore
                                      click={() => setFieldValue('entries', removeCarrier(values.entries, carrier.id))}
                                    />
                                  )}
                                </div>
                              </Fragment>
                            );
                          })}
                        </McTable>
                        <ErrorMessage className="EventsDrawer__error" name="entries" component="div" />
                      </div>
                    </>
                  )}
                </McTabBar>
              </div>
              <div className={styles.buttons}>
                <McButton type="button" appearance="neutral" click={() => onClose()}>
                  <FormattedMessage id="cancel" />
                </McButton>
                <McButton disabled={isSubmitting} type="submit" appearance="primary">
                  <FormattedMessage
                    id={isSubmitting ? 'updateRuleDrawerFormSubmitting' : 'updateRuleDrawerFormSubmit'}
                  />
                </McButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
};

const printTotal = (moves: number, percentage: number) => {
  return moves && percentage ? moves + Math.ceil((moves * percentage) / 100) : '-';
};

export default AddDefaultValuesDrawer;
